import jsPDF from 'jspdf';
import dayjs from 'dayjs';

function getInitials(fullName) {
    if (!fullName) return '';
    return fullName
        .split(' ')
        .filter(Boolean)
        .map((w) => w[0].toUpperCase())
        .join('');
}

function parseClientInfo(headerStr) {
    const info = { clientName: '', allergies: '', practitioner: '', diagnosis: '', dob: '' };
    if (!headerStr) return info;

    headerStr.split('\n').forEach((line) => {
        const colonIdx = line.indexOf(':');
        if (colonIdx === -1) return;
        const rawKey = line.slice(0, colonIdx).trim().toLowerCase();
        const value = line.slice(colonIdx + 1).trim();

        if (rawKey.includes('client') && rawKey.includes('name')) {
            info.clientName = value;
        } else if (rawKey.includes('allerg')) {
            info.allergies = value;
        } else if (rawKey.includes('practitioner') || rawKey.includes('physician') || rawKey.includes('healthcare')) {
            info.practitioner = value;
        } else if (rawKey.includes('diagnosis')) {
            info.diagnosis = value;
        } else if (rawKey === 'dob') {
            info.dob = value;
        }
    });
    return info;
}

export function generateMARPdf(table, teamInfo) {
    // -----------------------------------------------------------------------
    // Page geometry — Landscape A4 (297 x 210 mm)
    // -----------------------------------------------------------------------
    const PW = 297; // page width  (landscape)
    const PH = 210; // page height (landscape)
    const ML = 7; // margin left
    const MR = 7; // margin right
    const MT = 7; // margin top

    const USABLE_W = PW - ML - MR; // 283 mm — much wider than portrait

    const MED_COL_W = 45; // Medication name column
    const HOUR_COL_W = 14; // Hour column
    const DAY_COUNT = 31;
    const DAY_COL_W = (USABLE_W - MED_COL_W - HOUR_COL_W) / DAY_COUNT; // ~7.23 mm

    const COL_HDR_H = 7; // column header row height
    const MED_ROW_H = 8; // medication name row (initials go here)
    const SUB_ROW_H = 5; // frequency / dosage sub-rows
    const FOOTER_H = 48; // footer block height
    const HEADER_BLOCK_H = 18;

    // -----------------------------------------------------------------------
    // Build medication data
    // -----------------------------------------------------------------------
    const medColIdx = table.medicationColumnIndex;
    const medOrders = table.medicationOrders || {};

    // medMap: { [medName]: { dosage, frequency, days: { [dayNum]: initials[] } } }
    const medMap = {};

    if (typeof medColIdx === 'number' && Array.isArray(table.data)) {
        table.data.forEach((row) => {
            const dateStr = row[0]?.value || '';
            const staffName = row[2]?.value || '';
            const medName = row[medColIdx]?.value || '';
            if (!medName || !dateStr) return;

            // getStandardDate() returns en-CA locale format: YYYY-MM-DD (e.g. "2024-05-01")
            // Parse with plain dayjs() — ISO format, no custom format string needed
            const parsed = dayjs(dateStr);
            const dayNum = parsed.isValid() ? parsed.date() : null;
            if (!dayNum) return;

            if (!medMap[medName]) {
                const order = medOrders[medName] || {};
                medMap[medName] = { dosage: order.dosage || '', frequency: order.frequency || '', days: {} };
            }
            if (!medMap[medName].days[dayNum]) {
                medMap[medName].days[dayNum] = [];
            }
            const initials = getInitials(staffName);
            if (initials && !medMap[medName].days[dayNum].includes(initials)) {
                medMap[medName].days[dayNum].push(initials);
            }
        });
    }

    const medications = Object.keys(medMap);

    // -----------------------------------------------------------------------
    // Header info
    // -----------------------------------------------------------------------
    const clientInfo = parseClientInfo(table.specialityHeader || '');
    const teamName = teamInfo?.name || '';

    const allDates = (table.data || []).map((r) => r[0]?.value).filter(Boolean);
    const sortedDates = [...new Set(allDates)].sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
    const dateFrom = sortedDates[0] || '';
    const dateTo = sortedDates[sortedDates.length - 1] || '';

    const parsedFirst = dayjs(allDates[0] || '');
    const monthYear = parsedFirst.isValid() ? parsedFirst.format('MMMM YYYY') : '';

    // -----------------------------------------------------------------------
    // Init PDF — LANDSCAPE
    // -----------------------------------------------------------------------
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setLineWidth(0.3);
    doc.setDrawColor(0, 0, 0);

    // Helpers
    const filledRect = (x, y, w, h, fillHex) => {
        if (fillHex) {
            const r = parseInt(fillHex.slice(0, 2), 16);
            const g = parseInt(fillHex.slice(2, 4), 16);
            const b = parseInt(fillHex.slice(4, 6), 16);
            doc.setFillColor(r, g, b);
            doc.rect(x, y, w, h, 'FD');
        } else {
            doc.rect(x, y, w, h);
        }
    };

    const cellText = (text, x, y, w, h) => {
        doc.text(String(text || ''), x + w / 2, y + h / 2 + 1.5, { align: 'center' });
    };

    const leftText = (text, x, y, h, pad) => {
        const p = pad !== undefined ? pad : 1.5;
        doc.text(String(text || ''), x + p, y + h / 2 + 1.5);
    };

    // -----------------------------------------------------------------------
    // Header block
    // -----------------------------------------------------------------------
    let curY = MT;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('MEDICATION ADMINISTRATION RECORD (MAR)', PW / 2, curY + 5, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    if (teamName) doc.text(teamName, PW / 2, curY + 10, { align: 'center' });
    if (monthYear) doc.text(monthYear, PW / 2, curY + 15, { align: 'center' });

    curY += HEADER_BLOCK_H;

    // -----------------------------------------------------------------------
    // Column header row
    // -----------------------------------------------------------------------
    doc.setLineWidth(0.4);

    // MEDICATION
    filledRect(ML, curY, MED_COL_W, COL_HDR_H, 'e9ecef');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    cellText('MEDICATION', ML, curY, MED_COL_W, COL_HDR_H);

    // HOUR
    filledRect(ML + MED_COL_W, curY, HOUR_COL_W, COL_HDR_H, 'e9ecef');
    cellText('HOUR', ML + MED_COL_W, curY, HOUR_COL_W, COL_HDR_H);

    // Day numbers 1–31
    doc.setFontSize(5.5);
    for (let d = 1; d <= 31; d++) {
        const x = ML + MED_COL_W + HOUR_COL_W + (d - 1) * DAY_COL_W;
        filledRect(x, curY, DAY_COL_W, COL_HDR_H, 'e9ecef');
        cellText(String(d), x, curY, DAY_COL_W, COL_HDR_H);
    }

    curY += COL_HDR_H;

    // -----------------------------------------------------------------------
    // Medication rows
    // -----------------------------------------------------------------------
    doc.setLineWidth(0.3);

    medications.forEach((medName) => {
        const med = medMap[medName];
        const blockH = MED_ROW_H + SUB_ROW_H + SUB_ROW_H;

        // New page check (leave room for footer)
        if (curY + blockH > PH - FOOTER_H - 8) {
            doc.addPage();
            curY = MT;
        }

        // -- Medication name row (staff initials in each day cell) --
        filledRect(ML, curY, MED_COL_W, MED_ROW_H, 'f8f9fa');
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        const nameLines = doc.splitTextToSize(medName, MED_COL_W - 2);
        doc.text(nameLines[0] || '', ML + 1.5, curY + 3.5);
        if (nameLines[1]) doc.text(nameLines[1], ML + 1.5, curY + 7);

        filledRect(ML + MED_COL_W, curY, HOUR_COL_W, MED_ROW_H, 'f8f9fa');

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        for (let d = 1; d <= 31; d++) {
            const x = ML + MED_COL_W + HOUR_COL_W + (d - 1) * DAY_COL_W;
            filledRect(x, curY, DAY_COL_W, MED_ROW_H);
            const entries = med.days[d] || [];
            if (entries.length) cellText(entries[0], x, curY, DAY_COL_W, MED_ROW_H);
        }

        curY += MED_ROW_H;

        // -- Frequency sub-row --
        doc.rect(ML, curY, MED_COL_W, SUB_ROW_H);
        doc.setFontSize(5);
        doc.setFont('helvetica', 'italic');
        leftText('Freq: ' + (med.frequency || '________________'), ML, curY, SUB_ROW_H);

        doc.rect(ML + MED_COL_W, curY, HOUR_COL_W, SUB_ROW_H);
        for (let d = 1; d <= 31; d++) {
            doc.rect(ML + MED_COL_W + HOUR_COL_W + (d - 1) * DAY_COL_W, curY, DAY_COL_W, SUB_ROW_H);
        }
        curY += SUB_ROW_H;

        // -- Dosage sub-row --
        doc.rect(ML, curY, MED_COL_W, SUB_ROW_H);
        leftText('Dose: ' + (med.dosage || '________________'), ML, curY, SUB_ROW_H);

        doc.rect(ML + MED_COL_W, curY, HOUR_COL_W, SUB_ROW_H);
        for (let d = 1; d <= 31; d++) {
            doc.rect(ML + MED_COL_W + HOUR_COL_W + (d - 1) * DAY_COL_W, curY, DAY_COL_W, SUB_ROW_H);
        }
        curY += SUB_ROW_H + 1;
    });

    // -----------------------------------------------------------------------
    // Footer
    // -----------------------------------------------------------------------
    const footerY = Math.max(curY + 4, PH - FOOTER_H - MT);
    doc.setLineWidth(0.4);
    doc.rect(ML, footerY, USABLE_W, FOOTER_H);

    const FL = FOOTER_H / 6;
    const HALF = USABLE_W / 2;

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setLineWidth(0.2);

    // Row 1 — Dates
    doc.text(
        'Dates of Administration from: ' + (dateFrom || '__________') + '   to: ' + (dateTo || '__________'),
        ML + 2,
        footerY + FL - 2
    );
    doc.line(ML, footerY + FL, ML + USABLE_W, footerY + FL);

    // Row 2 — Manager check
    doc.text('Manager Weekly Check: _______________________________________________', ML + 2, footerY + FL * 2 - 2);
    doc.line(ML, footerY + FL * 2, ML + USABLE_W, footerY + FL * 2);

    // Row 3 — Physician | Diagnosis
    doc.text('Physician: ' + (clientInfo.practitioner || '_________________________'), ML + 2, footerY + FL * 3 - 2);
    doc.text(
        'Diagnosis: ' + (clientInfo.diagnosis || '_________________________'),
        ML + HALF + 2,
        footerY + FL * 3 - 2
    );
    doc.line(ML + HALF, footerY + FL * 2, ML + HALF, footerY + FL * 3);
    doc.line(ML, footerY + FL * 3, ML + USABLE_W, footerY + FL * 3);

    // Row 4 — Allergies | Name
    doc.text('Allergies: ' + (clientInfo.allergies || '_________________________'), ML + 2, footerY + FL * 4 - 2);
    doc.text('Name: ' + (clientInfo.clientName || '_________________________'), ML + HALF + 2, footerY + FL * 4 - 2);
    doc.line(ML + HALF, footerY + FL * 3, ML + HALF, footerY + FL * 4);
    doc.line(ML, footerY + FL * 4, ML + USABLE_W, footerY + FL * 4);

    // Row 5 — DOB | ID Number
    doc.text('DOB: ' + (clientInfo.dob || '_________________________'), ML + 2, footerY + FL * 5 - 2);
    doc.text('ID Number: _________________________', ML + HALF + 2, footerY + FL * 5 - 2);
    doc.line(ML + HALF, footerY + FL * 4, ML + HALF, footerY + FL * 5);
    doc.line(ML, footerY + FL * 5, ML + USABLE_W, footerY + FL * 5);

    // Row 6 — Staff initials key
    doc.setFont('helvetica', 'bold');
    doc.text('Staff Initials Key:', ML + 2, footerY + FL * 6 - 2);
    doc.setFont('helvetica', 'normal');
    const allInitials = medications
        .flatMap((m) => Object.values(medMap[m].days).flat())
        .filter((v, i, a) => v && a.indexOf(v) === i)
        .join('   ');
    if (allInitials) doc.text(allInitials, ML + 32, footerY + FL * 6 - 2);

    // -----------------------------------------------------------------------
    // Save
    // -----------------------------------------------------------------------
    const safeName = (clientInfo.clientName || 'client').replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-');
    doc.save('MAR-' + safeName + '.pdf');
}
